import { useEffect, useState, useMemo, memo, useRef } from 'react';
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  TouchSensor,
  closestCorners,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  defaultDropAnimationSideEffects,
  DropAnimation
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Link, Folder } from '../api';
import type { TranslationKey } from '../i18n';
import InlineQRCode from './InlineQRCode';

import InlineAnalytics from './InlineAnalytics';
import CreateFolderModal from './CreateFolderModal';
import EditFolderModal from './EditFolderModal';
import MoveToFolderModal from './MoveToFolderModal';

interface Props {
  links: Link[];
  shortDomain?: string;
  deletingSlug?: string | null;
  onEdit: (link: Link) => void;
  onDelete: (slug: string) => void;
  onRefresh: () => void;
  folders?: Folder[];
  onCreateFolder?: () => void;
  onDeleteFolder?: (folderId: number) => void;
  onEditFolder?: (folderId: number, name: string) => Promise<void>;
  onReorderFolders?: (order: number[]) => Promise<void>;
  onReorderLinks?: (slugs: string[]) => Promise<void>;
  onUpdateLinkPosition?: (slug: string, position: { beforeSlug?: string | null; afterSlug?: string | null }) => Promise<void>;
  onMoveLink?: (link: Link) => void;
  onUpdateLinkFolder?: (slug: string, folderId: number | null) => Promise<void>;
  t: (key: TranslationKey, vars?: Record<string, string>) => string;
  locale: string;
  authToken?: string;
  search?: string;
  onSearchChange?: (value: string) => void;
}

// Display helpers
const displayDestination = (href: string) => {
  try {
    const parsed = new URL(href);
    const rest = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    return { host: parsed.hostname, rest: rest === '/' ? '' : rest };
  } catch {
    return { host: href, rest: '' };
  }
};

const useIsMobile = (query = '(max-width: 560px)') => {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener?.('change', onChange);
    return () => media.removeEventListener?.('change', onChange);
  }, [query]);
  return matches;
};

// --- Child Components ---

interface LinkRowProps {
  link: Link;
  shortDomain?: string;
  authToken?: string;
  t: (key: TranslationKey) => string;
  onEdit: (link: Link) => void;
  onDelete: (slug: string) => void;
  isMobile: boolean;
  deletingSlug?: string | null;
  openMenuSlug: string | null;
  setOpenMenuSlug: (slug: string | null) => void;
  expandedSlug: string | null;
  setExpandedSlug: (slug: string | null) => void;
  expandType: 'qr' | 'analytics' | null;
  setExpandType: (type: 'qr' | 'analytics' | null) => void;
  onMoveLink?: (link: Link) => void;
  isOverlay?: boolean;
}

const LinkRow = memo(({
  link, shortDomain, authToken, t, onEdit, onDelete, isMobile, deletingSlug,
  openMenuSlug, setOpenMenuSlug, expandedSlug, setExpandedSlug,
  expandType, setExpandType, onMoveLink, isOverlay
}: LinkRowProps) => {
  const fallbackOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const displayDomain = shortDomain?.replace(/\/$/, '');
  const shortParts = buildShortUrlParts(link.slug, displayDomain || fallbackOrigin);
  const dest = displayDestination(link.destination);

  // Sortable Hook
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: link.slug,
    data: {
      type: 'Link',
      link,
    }
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const copyToClipboard = async (value: string) => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(value);
      }
    } catch (error) { console.error(error); }
  };

  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuSlug === link.slug && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuSlug(null);
      }
    };

    if (openMenuSlug === link.slug) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuSlug, link.slug, setOpenMenuSlug]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`link-grid-row ${isDragging ? 'dragging' : ''} ${isOverlay ? 'drag-overlay' : ''}`}
      {...attributes}
    >
      {/* Desktop Drag Handle */}
      <div className="drag-handle hide-mobile" {...listeners} style={{ cursor: 'grab', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="9" cy="12" r="1" />
          <circle cx="9" cy="5" r="1" />
          <circle cx="9" cy="19" r="1" />
          <circle cx="15" cy="12" r="1" />
          <circle cx="15" cy="5" r="1" />
          <circle cx="15" cy="19" r="1" />
        </svg>
      </div>

      {/* Short Link Column */}
      <div className="link-grid-cell">
        {/* Desktop: Full URL */}
        <div className="short-url-cell hide-mobile">
          <div className="drag-handle-desktop" {...listeners} style={{ cursor: isDragging ? 'grabbing' : 'grab', marginRight: '0.5rem', color: 'var(--muted)', display: 'flex', alignItems: 'center' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          </div>
          <div className="short-url-content">
            <span className="scheme">{shortParts.scheme}</span>
            <span className="host">{shortParts.host}</span>
            <span className="path">{shortParts.path}</span>
          </div>
        </div>

        {/* Mobile: Compact with icon + slug */}
        <div className="short-url-mobile show-mobile">
          <div {...listeners} style={{ cursor: isDragging ? 'grabbing' : 'grab', display: 'flex', touchAction: 'none', padding: '8px', margin: '-8px' }}>
            <svg className="short-url-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          </div>
          <span className="short-url-slug">{shortParts.path}</span>
        </div>

        {link.description && <div className="link-desc">{link.description}</div>}

        {/* Expanded Content (QR/Analytics) */}
        {expandedSlug === link.slug && !isOverlay && (
          <div className="expanded-content">
            {expandType === 'qr' && (
              <div className="expanded-panel">
                <InlineQRCode
                  link={link}
                  shortDomain={displayDomain || fallbackOrigin}
                  onClose={() => setExpandedSlug(null)}
                />
              </div>
            )}
            {expandType === 'analytics' && (
              <div className="expanded-panel">
                <InlineAnalytics
                  link={link}
                  token={authToken || ''}
                  onClose={() => setExpandedSlug(null)}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Destination Column */}
      <div className="link-grid-cell destination-cell">
        <a href={link.destination} target="_blank" rel="noopener noreferrer" className="destination-link">
          <span className="dest-host">{dest.host}</span>
          <span className="dest-rest">{dest.rest}</span>
        </a>
        <div className="link-date">{new Date(link.createdAt).toLocaleDateString()}</div>
      </div>

      {/* Clicks Column */}
      <div className="link-grid-cell clicks-cell">
        <span className="clicks-badge">
          {link.clicks}
        </span>
      </div>

      {/* Actions Column */}
      <div className="link-grid-cell actions">
        <div className="action-buttons">
          <button
            className="ghost-button icon-button copy-action-btn-v2"
            onClick={(e) => { e.stopPropagation(); copyToClipboard(shortParts.copyValue); }}
            title={t('table.copy')}
            type="button"
            style={{
              backgroundColor: 'transparent',
              border: '1px solid var(--border)',
              width: '32px',
              height: '32px',
              padding: 0,
              minHeight: 0,
              aspectRatio: '1/1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'none'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          </button>
          <div className="menu-wrapper" style={{ position: 'relative' }} ref={menuRef}>
            <button
              className="ghost-button icon-button"
              onClick={(e) => { e.stopPropagation(); setOpenMenuSlug(openMenuSlug === link.slug ? null : link.slug); }}
            >
              ⋮
            </button>
            {openMenuSlug === link.slug && !isOverlay && (
              <div className="actions-dropdown right-aligned" style={{ zIndex: 100 }}>
                <button className="dropdown-item" onClick={() => handleShowAnalytics(link, setExpandedSlug, setExpandType, setOpenMenuSlug)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>
                  Analytics
                </button>
                <button className="dropdown-item" onClick={() => handleShowQR(link, setExpandedSlug, setExpandType, setOpenMenuSlug)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></svg>
                  QR Code
                </button>
                <button className="dropdown-item" onClick={() => { onEdit(link); setOpenMenuSlug(null); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                  {t('table.edit')}
                </button>
                <button className="dropdown-item danger" onClick={() => { onDelete(link.slug); setOpenMenuSlug(null); }} disabled={deletingSlug === link.slug}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                  {deletingSlug === link.slug ? t('table.deleting') : t('table.delete')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

// Helper to build URL parts
const buildShortUrlParts = (slug: string, base: string) => {
  try {
    const url = new URL(base);
    return {
      scheme: `${url.protocol}//`,
      host: url.hostname,
      path: `/${slug}`,
      copyValue: `${base}/${slug}`
    };
  } catch {
    return { scheme: '', host: base, path: `/${slug}`, copyValue: `${base}/${slug}` };
  }
};

// Helpers for expand logic reuse
const handleShowQR = (
  link: Link,
  setExpanded: (slug: string | null) => void,
  setExpandType: (type: 'qr' | 'analytics' | null) => void,
  setOpenMenu: (slug: string | null) => void
) => {
  setExpanded(link.slug);
  setExpandType('qr');
  setOpenMenu(null);
};
const handleShowAnalytics = (
  link: Link,
  setExpanded: (slug: string | null) => void,
  setExpandType: (type: 'qr' | 'analytics' | null) => void,
  setOpenMenu: (slug: string | null) => void
) => {
  setExpanded(link.slug);
  setExpandType('analytics');
  setOpenMenu(null);
};


// --- Folder Group Component ---
interface FolderGroupProps {
  id: string; // 'uncategorized' | number
  folderName: string;
  links: Link[];
  isExpanded: boolean;
  onToggle: () => void;
  isHighlighted?: boolean;
  rowProps: Omit<LinkRowProps, 'link'>;
  onDeleteFolder?: (folderId: number) => void;
  onRequestRename?: (folderId: number, name: string) => void; // Replaces onEditFolder direct call
}

const FolderGroup = memo(({ id, folderName, links, isExpanded, onToggle, isHighlighted, rowProps, onDeleteFolder, onRequestRename }: FolderGroupProps) => {
  const isSortable = id !== 'uncategorized';

  const {
    attributes: sortableAttributes,
    listeners: sortableListeners,
    setNodeRef: setSortableRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging: isFolderDragging
  } = useSortable({
    id: `folder-sort-${id}`,
    data: { type: 'FolderSort', id },
    disabled: !isSortable
  });

  const { setNodeRef: setDroppableRef } = useDroppable({
    id: `folder-${id}`,
    data: { type: 'Folder', folderId: id === 'uncategorized' ? null : Number(id) }
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    position: 'relative' as const,
    zIndex: isFolderDragging ? 999 : 'auto',
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (id !== 'uncategorized' && onDeleteFolder) {
      // Direct delete without confirmation as requested
      onDeleteFolder(Number(id));
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (id !== 'uncategorized' && onRequestRename) {
      onRequestRename(Number(id), folderName);
    }
  };

  return (
    <div
      ref={setSortableRef}
      style={style}
      className={`folder-section ${isExpanded ? 'expanded' : ''} ${isHighlighted ? 'drag-over' : ''} ${isFolderDragging ? 'dragging' : ''}`}
    >
      <div
        ref={setDroppableRef}
        className="folder-header"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <span className="folder-toggle-icon">
          <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor"><path d="M2 1l4 3-4 3V1z" /></svg>
        </span>

        <span
          className="folder-drag-handle"
          ref={setActivatorNodeRef}
          {...sortableListeners}
          {...sortableAttributes}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            touchAction: 'none',
            cursor: isSortable ? 'grab' : 'default',
            // flex: 1 removed to prevent grabbing empty space
          }}
        >
          {id !== 'uncategorized' && (
            <span className="folder-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2-2z" />
              </svg>
            </span>
          )}
          <span className="folder-name">{folderName}</span>
        </span>

        {id !== 'uncategorized' && (
          <div className="folder-actions" style={{ marginLeft: 'auto', display: 'flex', gap: '0.25rem' }}>
            {onRequestRename && (
              <button className="folder-action-btn" onClick={handleEdit} title="Renommer le dossier">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </button>
            )}
            {onDeleteFolder && (
              <button className="folder-action-btn delete" onClick={handleDelete} title="Supprimer le dossier">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
            )}
          </div>
        )}

        <span className="folder-count-badge">{links.length}</span>
      </div>

      {isExpanded && (
        <div className="folder-body">
          <SortableContext
            items={links.map(l => l.slug)}
            strategy={verticalListSortingStrategy}
          >
            {links.length === 0 ? (
              <div style={{ padding: '1rem', color: 'var(--muted)', textAlign: 'center', fontSize: '0.9rem' }}>
                {rowProps.t('folder.empty')}
              </div>
            ) : (
              links.map(link => (
                <LinkRow
                  key={link.slug}
                  link={link}
                  {...rowProps}
                />
              ))
            )}
          </SortableContext>
        </div>
      )}
    </div>
  );
});


const LinkTable = ({ links, shortDomain, deletingSlug, onEdit, onDelete, onRefresh, onCreateFolder, onDeleteFolder, onEditFolder, onReorderFolders, onReorderLinks, onUpdateLinkPosition, folders = [], onMoveLink, onUpdateLinkFolder, t, locale, authToken, search, onSearchChange }: Props) => {
  const isMobile = useIsMobile();
  const [openMenuSlug, setOpenMenuSlug] = useState<string | null>(null);
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);
  const [expandType, setExpandType] = useState<'qr' | 'analytics' | null>(null);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  const [overFolderId, setOverFolderId] = useState<string | null>(null);
  const [editingFolder, setEditingFolder] = useState<{ id: number; name: string } | null>(null);

  // Folder Expansion State
  const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({
    'uncategorized': true
  });

  // Ref to save expanded state during folder drag
  const savedExpandedRef = useRef<Record<string, boolean> | null>(null);

  // Auto-expand folders on load
  useEffect(() => {
    const nextState = { ...expandedFolders };
    folders.forEach(f => {
      if (nextState[String(f.id)] === undefined) {
        nextState[String(f.id)] = true;
      }
    });
    setExpandedFolders(nextState);
    setExpandedFolders(nextState);
  }, [folders]);

  const folderSortIds = useMemo(() => folders.map(f => `folder-sort-${f.id}`), [folders]);

  const toggleFolder = (id: string) => {
    setExpandedFolders(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Group Links
  const groupedLinks = useMemo(() => {
    const groups: Record<string, Link[]> = { 'uncategorized': [] };
    folders.forEach(f => groups[String(f.id)] = []);

    links.forEach(link => {
      const fid = link.folderId ? String(link.folderId) : 'uncategorized';
      if (!groups[fid] && fid === 'uncategorized') groups['uncategorized'].push(link);
      else if (groups[fid]) groups[fid].push(link);
      else groups['uncategorized'].push(link);
    });

    // Sort each group by position then updated_at
    Object.values(groups).forEach(group => {
      group.sort((a, b) => {
        // First by position (asc)
        if (a.position !== undefined && b.position !== undefined) {
          if (a.position !== b.position) return a.position - b.position;
        }
        if (a.position !== undefined && b.position === undefined) return -1;
        if (a.position === undefined && b.position !== undefined) return 1;

        // Then by updated_at (desc)
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      });
    });

    return groups;
  }, [links, folders]);

  const activeLink = useMemo(() => links.find(l => l.slug === activeId), [activeId, links]);

  // DnD Sensors - increased distance to reduce accidental drags
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 10 } })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setOpenMenuSlug(null); // Close menus

    // If dragging a folder, collapse all folders temporarily
    if (event.active.data.current?.type === 'FolderSort') {
      savedExpandedRef.current = { ...expandedFolders };
      // Collapse all folders
      const collapsed: Record<string, boolean> = {};
      Object.keys(expandedFolders).forEach(key => {
        collapsed[key] = false;
      });
      setExpandedFolders(collapsed);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) {
      setOverFolderId(null);
      return;
    }

    // If dragging a folder, don't highlight other folders (no folder nesting)
    if (active.data.current?.type === 'FolderSort') {
      setOverFolderId(null);
      return;
    }

    const overId = String(over.id);
    if (overId.startsWith('folder-')) {
      // Directly over a folder
      setOverFolderId(overId.replace('folder-', ''));
    } else {
      // Over a link - find which folder it belongs to
      const overLink = links.find(l => l.slug === overId);
      if (overLink) {
        setOverFolderId(overLink.folderId ? String(overLink.folderId) : 'uncategorized');
      } else {
        setOverFolderId(null);
      }
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveId(null);
    setOverFolderId(null);
    const { active, over } = event;

    // Handle Folder Reordering
    if (active.data.current?.type === 'FolderSort') {
      // Restore folder expansion state
      if (savedExpandedRef.current) {
        setExpandedFolders(savedExpandedRef.current);
        savedExpandedRef.current = null;
      }

      if (!over) return;

      if (active.id !== over.id) {
        const oldIndex = folders.findIndex(f => `folder-sort-${f.id}` === active.id);
        const newIndex = folders.findIndex(f => `folder-sort-${f.id}` === over.id);

        if (oldIndex !== -1 && newIndex !== -1 && onReorderFolders) {
          const newOrder = [...folders];
          const [moved] = newOrder.splice(oldIndex, 1);
          newOrder.splice(newIndex, 0, moved);

          const orderIds = newOrder.map(f => f.id);
          await onReorderFolders(orderIds);
        }
      }
      return;
    }

    if (!over) return;

    const activeSlug = active.id as string;
    const link = links.find(l => l.slug === activeSlug);

    // If it's a link drag
    if (link) {
      const overId = String(over.id);

      // Check if dropped on another link
      const overLink = links.find(l => l.slug === overId);

      if (overLink) {
        // Normalize folder IDs (treat null and undefined as same)
        const linkFolderId = link.folderId ?? null;
        const overLinkFolderId = overLink.folderId ?? null;

        console.log('[DnD] Dropped', activeSlug, 'on', overId, 'linkFolderId:', linkFolderId, 'overLinkFolderId:', overLinkFolderId);

        // Same folder? Reorder within folder
        if (linkFolderId === overLinkFolderId) {
          const fid = linkFolderId ? String(linkFolderId) : 'uncategorized';
          const folderLinks = groupedLinks[fid] || [];
          const oldIndex = folderLinks.findIndex(l => l.slug === activeSlug);
          const newIndex = folderLinks.findIndex(l => l.slug === overId);

          console.log('[DnD] Same folder reorder - oldIndex:', oldIndex, 'newIndex:', newIndex, 'folderLinks count:', folderLinks.length);

          if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
            // Calculate before/after slugs for fractional indexing
            const reordered = [...folderLinks];
            const [moved] = reordered.splice(oldIndex, 1);
            reordered.splice(newIndex, 0, moved);

            // Determine neighbors for fractional position
            const beforeSlug = newIndex > 0 ? reordered[newIndex - 1].slug : null;
            const afterSlug = newIndex < reordered.length - 1 ? reordered[newIndex + 1].slug : null;

            console.log('[DnD] Calling onUpdateLinkPosition:', activeSlug, 'before:', beforeSlug, 'after:', afterSlug);

            // Use fractional indexing if available, fallback to old method
            if (onUpdateLinkPosition) {
              onUpdateLinkPosition(activeSlug, { beforeSlug, afterSlug });
            } else if (onReorderLinks) {
              console.log('[DnD] Fallback to onReorderLinks');
              onReorderLinks(reordered.map(l => l.slug));
            }
          } else {
            console.log('[DnD] Skipped - same position or index not found');
          }
        } else {
          // Different folder - move link to target folder
          const targetFolderId = overLink.folderId || null;
          onUpdateLinkFolder?.(activeSlug, targetFolderId);
          // Removed onRefresh() to prevent scroll reset

        }
      } else if (overId.startsWith('folder-') && !overId.startsWith('folder-sort-')) {
        // Dropped on a folder header
        const rawId = overId.replace('folder-', '');
        const targetFolderId = rawId === 'uncategorized' ? null : Number(rawId);

        if (link.folderId !== targetFolderId) {
          onUpdateLinkFolder?.(activeSlug, targetFolderId);
          // Removed onRefresh() to prevent scroll reset

        }
      }
    }
  };

  const dropAnimation: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0.5'
        }
      }
    })
  };

  const rowProps = useMemo(() => ({
    shortDomain, t, onEdit, onDelete, isMobile, deletingSlug,
    openMenuSlug, setOpenMenuSlug,
    expandedSlug, setExpandedSlug,
    expandType, setExpandType, onMoveLink, isOverlay: false,
    authToken
  }), [
    shortDomain, t, onEdit, onDelete, isMobile, deletingSlug,
    openMenuSlug, setOpenMenuSlug, expandedSlug, setExpandedSlug,
    expandType, setExpandType, onMoveLink, authToken
  ]);

  return (
    <div className="link-table-container">
      <div className="card-header">
        <div className="card-header-title-row">
          <h2>{links.length} {t(links.length <= 1 ? 'table.linkSingular' : 'table.linkPlural')}</h2>
          {onCreateFolder && (
            <button className="add-folder-btn" onClick={onCreateFolder}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span>Dossier</span>
            </button>
          )}

          {/* Search Input - Inline with title */}
          {onSearchChange && (
            <div className="table-search-inline">
              <svg className="table-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                className="table-search-input"
                placeholder={t('search.placeholder')}
                value={search || ''}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
          )}
        </div>
      </div>

      <div className="link-grid-header hide-mobile">
        <div></div>
        <div>{t('table.slug')}</div>
        <div>{t('table.destination')}</div>
        <div>{t('table.clicks')}</div>
        <div style={{ textAlign: 'right' }}>{t('table.actions')}</div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={folderSortIds} strategy={verticalListSortingStrategy}>
          {folders.map(folder => (
            <FolderGroup
              key={folder.id}
              id={String(folder.id)}
              folderName={folder.name}
              links={groupedLinks[String(folder.id)]}
              isExpanded={!!expandedFolders[String(folder.id)]}
              onToggle={() => toggleFolder(String(folder.id))}
              isHighlighted={overFolderId === String(folder.id)}
              onDeleteFolder={onDeleteFolder}
              onRequestRename={(id, name) => setEditingFolder({ id, name })}
              rowProps={rowProps}
            />
          ))}
        </SortableContext>

        <FolderGroup
          id="uncategorized"
          folderName={t('folder.uncategorized')}
          links={groupedLinks['uncategorized']}
          isExpanded={!!expandedFolders['uncategorized']}
          onToggle={() => toggleFolder('uncategorized')}
          isHighlighted={overFolderId === 'uncategorized'}
          rowProps={rowProps}
        />

        <DragOverlay dropAnimation={dropAnimation}>
          {activeLink ? (
            <div className="drag-preview">
              <svg className="drag-preview-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              <span className="drag-preview-slug">/{activeLink.slug}</span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {editingFolder && onEditFolder && (
        <EditFolderModal
          folderId={editingFolder.id}
          initialName={editingFolder.name}
          onClose={() => setEditingFolder(null)}
          onSubmit={onEditFolder}
        />
      )}
    </div>
  );
};

export default LinkTable;
